import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import FlexSearch from 'flexsearch';
import {
  CheckCircle,
  Info,
  AlertTriangle,
  HelpCircle,
  Search,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { HCard, HInput, SectionLabel } from '../h';
import { MessageUsCard } from './MessageUsCard';
import docs from '../../data/docs.json';

/* Category display metadata — colour pairs from v9 spec 6.1. */
const CATEGORIES = [
  {
    key: 'Getting Started',
    label: 'Getting started',
    iconBg: '#dcfce7',
    iconColor: '#047857',
    Icon: CheckCircle,
  },
  {
    key: 'Understanding Higlo',
    label: 'Understanding Higlo',
    iconBg: '#dbeafe',
    iconColor: '#1e40af',
    Icon: Info,
  },
  {
    key: 'Troubleshooting',
    label: 'Troubleshooting',
    iconBg: '#fef3c7',
    iconColor: '#b45309',
    Icon: AlertTriangle,
  },
  {
    key: 'FAQ',
    label: 'FAQ',
    iconBg: '#f3e8ff',
    iconColor: '#6d28d9',
    Icon: HelpCircle,
  },
];

/* Rough minute-read estimate (200 wpm) from article content. */
function minutesToRead(content) {
  if (!content) return 1;
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function articleSummary(article) {
  if (article.summary) return article.summary;
  const stripped = (article.content || '')
    .replace(/^#.*$/gm, '')
    .replace(/[#*_`>]/g, '')
    .trim();
  const firstParagraph = stripped.split(/\n\s*\n/).find(Boolean) || '';
  if (!firstParagraph) return `${minutesToRead(article.content)} min read`;
  const oneLine = firstParagraph.replace(/\s+/g, ' ').trim();
  return oneLine.length > 110 ? `${oneLine.slice(0, 107)}…` : oneLine;
}

/**
 * HelpTab — v9 spec 6.1 / 6.2.
 * Renders as a tab body (no shell wrapper — AppShell provides chrome).
 *
 * Props:
 *   unreadCount   number of unread support replies (drives the badge + copy)
 *   onNavigate    optional callback(path) — used in previews where router
 *                 isn't mounted. Falls back to react-router's useNavigate.
 */
export function HelpTab({ unreadCount = 0, onNavigate }) {
  const [selectedCategory, setSelectedCategory] = React.useState(
    CATEGORIES[0].key,
  );
  const [query, setQuery] = React.useState('');

  // Use react-router if available, otherwise fall back to onNavigate prop.
  let routerNavigate = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    routerNavigate = useNavigate();
  } catch (_err) {
    routerNavigate = null;
  }
  const navigate = React.useCallback(
    (path) => {
      if (onNavigate) return onNavigate(path);
      if (routerNavigate) return routerNavigate(path);
      return undefined;
    },
    [onNavigate, routerNavigate],
  );

  const articles = Array.isArray(docs.articles) ? docs.articles : [];

  /* Count articles per category for the tile subtitles. */
  const countsByCategory = React.useMemo(() => {
    const counts = {};
    for (const cat of CATEGORIES) counts[cat.key] = 0;
    for (const a of articles) {
      if (counts[a.category] != null) counts[a.category] += 1;
    }
    return counts;
  }, [articles]);

  /* FlexSearch index over every article's full searchable body. Mirrors
     OLD DocumentationViewer: each article is indexed by title + category
     + keywords + tags + searchText (the stripped-markdown body), so a
     query like "sleep" or "feedback strip" finds articles even when the
     term only appears in the body. Built once. */
  const searchIndex = React.useMemo(() => {
    const index = new FlexSearch.Index({
      tokenize: 'forward',
      resolution: 9,
    });
    articles.forEach((article, i) => {
      const searchable = [
        article.title,
        article.category,
        ...(article.keywords || []),
        ...(article.tags || []),
        article.searchText || '',
      ].join(' ');
      index.add(i, searchable);
    });
    return index;
  }, [articles]);

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;

  /* When searching we show a flat result list across ALL categories
     (matches the OLD UX — category tiles disappear while you type).
     When the search field is empty the category tiles come back and
     the list is filtered to the selected tile. */
  const searchResults = React.useMemo(() => {
    if (!isSearching) return [];
    const hits = searchIndex.search(trimmedQuery, { limit: 20 });
    return hits.map((i) => articles[i]).filter(Boolean);
  }, [isSearching, trimmedQuery, searchIndex, articles]);

  const visibleArticles = React.useMemo(() => {
    if (isSearching) return searchResults;
    const pool = articles.filter((a) => a.category === selectedCategory);
    return [...pool].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  }, [isSearching, searchResults, articles, selectedCategory]);

  const selectedMeta =
    CATEGORIES.find((c) => c.key === selectedCategory) || CATEGORIES[0];

  return (
    <div className="px-4 pt-3 pb-6">
      <MessageUsCard
        unreadCount={unreadCount}
        onClick={() => navigate('/app/help/messages')}
      />

      {/* Search input — muted style, purely local filter. Icon + input sit
          in one flex row so there's no absolute-positioning overlap with
          the placeholder text. */}
      <label className="mb-4 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg min-h-[38px] px-3 focus-within:ring-2 focus-within:ring-slate-900 focus-within:ring-offset-1">
        <Search
          className="w-4 h-4 flex-shrink-0"
          style={{ color: '#64748b' }}
          strokeWidth={2}
        />
        <input
          type="search"
          placeholder="Search help articles"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 min-w-0 bg-transparent text-[14px] text-slate-900 placeholder:text-slate-400 outline-none border-none"
        />
      </label>

      {/* Featured "What setting up involves" — pulled out of the category
          list as a full-width entry point above the tiles. Hidden while
          searching. FUTURE: gate this on whether the account has finished
          setup (show for new/unset-up accounts, hide once set up). */}
      {!isSearching && (() => {
        const featured = articles.find(
          (a) => a.slug === 'getting-started/what-setup-involves',
        );
        if (!featured) return null;
        return (
          <button
            type="button"
            onClick={() => navigate(`/app/help/article/${featured.slug}`)}
            className="w-full mb-4 rounded-2xl p-4 flex items-center gap-3 text-left transition-colors hover:brightness-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
            style={{ background: '#FBF3E2', border: '1px solid #F0DFB8' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#E8A848' }}
            >
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[10px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: '#B45309' }}
              >
                New to Higlo?
              </div>
              <div className="text-[15px] font-semibold text-slate-900">
                What setting up involves
              </div>
              <div className="text-[12px] text-slate-600 mt-0.5">
                A visual walk-through — see how it works, for you and your parents.
              </div>
            </div>
            <ChevronRight
              className="w-5 h-5 flex-shrink-0"
              style={{ color: '#B45309' }}
              strokeWidth={2}
            />
          </button>
        );
      })()}

      {/* Category tiles only show when NOT searching. While the search
          field has any text we hide them and render a flat result list
          across all categories instead — matches OLD DocumentationViewer
          behaviour. Clearing the field restores the tiles. */}
      {!isSearching && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {CATEGORIES.map((cat) => {
            const isSelected = cat.key === selectedCategory;
            const Icon = cat.Icon;
            const count = countsByCategory[cat.key] ?? 0;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setSelectedCategory(cat.key)}
                className="rounded-2xl p-3 text-left bg-white transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
                style={{
                  border: isSelected
                    ? '2px solid #0f172a'
                    : '1px solid #e2e8f0',
                }}
                aria-pressed={isSelected}
              >
                <div
                  className="w-7 h-7 rounded-lg mb-2 flex items-center justify-center"
                  style={{ background: cat.iconBg }}
                >
                  <Icon
                    className="w-4 h-4"
                    style={{ color: cat.iconColor }}
                    strokeWidth={2}
                  />
                </div>
                <div className="text-[13px] font-semibold text-slate-900">
                  {cat.label}
                </div>
                <div className="text-[11px] text-slate-500">
                  {count} {count === 1 ? 'article' : 'articles'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Article list — section label switches between the category name
          and the search summary depending on mode. */}
      <SectionLabel className="mb-2 normal-case tracking-[0.14em]">
        {isSearching
          ? `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for "${trimmedQuery}"`
          : selectedMeta.label}
      </SectionLabel>
      <HCard className="overflow-hidden">
        {visibleArticles.length === 0 ? (
          <div className="px-4 py-6 text-[13px] text-slate-500 text-center">
            {isSearching
              ? `No results found for "${trimmedQuery}"`
              : 'No matching articles.'}
          </div>
        ) : (
          visibleArticles.map((article, idx) => {
            const isLast = idx === visibleArticles.length - 1;
            return (
              <button
                key={article.slug}
                type="button"
                onClick={() =>
                  navigate(`/app/help/article/${article.slug}`)
                }
                className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:bg-slate-50 ${
                  isLast ? '' : 'border-b border-slate-100'
                }`}
              >
                <div className="min-w-0 pr-3">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">
                    {article.title}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {articleSummary(article)}
                  </div>
                </div>
                <ChevronRight
                  className="w-4 h-4 flex-shrink-0 text-slate-400"
                  strokeWidth={2}
                />
              </button>
            );
          })
        )}
      </HCard>
    </div>
  );
}

export default HelpTab;
